import { useFormikContext } from 'formik'

import Styles from './Source.module.scss'
import { LottieProps } from 'react-lottie-player'

import { Values } from 'components/Wizards/schemas/TurnInto'
import { Form } from 'components/Wizards/Form/Form'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import React, { useCallback, useEffect, useState } from 'react'
import { WebsiteScrapingErrorCode, useFetchReference } from 'hooks/useFetchReference'
import { ChooseContentPlurality } from './components/ChooseContentPlurality/ChooseContentPlurality'
import { useModals } from 'almanac-ui/Modal'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { AudioOrVideoSource } from './components/AudioOrVideoSource/AudioOrVideoSource'
import { SourceType } from './Types'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useIsSourceValid } from './useIsSourceValid'
import { Loader, LoaderProps } from './components/Loader/Loader'

import { useTranscription } from 'hooks/queries/transcriptions/useTranscription'
import { useCreateTranscription } from 'hooks/queries/transcriptions/useCreateTranscription'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DocumentSource } from './components/DocumentSource/DocumentSource'
import { Doc } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromDoc'
import { ArticleClient } from 'api/clients/ArticleClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ChooseSourceType } from './components/ChooseSourceType/ChooseSourceType'

import Analyzing from 'blaze-components/Animations/Analyzing'
import Uploading from 'blaze-components/Animations/Uploading'
import Transcribing from 'blaze-components/Animations/Transcribing'
import { CAMPAIGN_FOCUS } from '../TurnIntoWizard'
import { WebsiteSource } from './components/WebsiteSource/WebsiteSource'
import { WebsiteScrapingError } from 'api/clients/BrandVoiceDetectionClient'
import { sendToApi } from 'api/client'

interface Props {
  initialStep: number
  isCampaign: boolean
  setIsCampaign: (value: boolean) => void
  onContinue: () => void
  sourceType: SourceType | null
  setSourceType: (value: SourceType) => void
  isFromDoc?: boolean
  initialIsCampaign?: boolean
}

export function getInputType(sourceType: SourceType | null): 'audio' | 'video' | 'file' | 'website' | null {
  if (!sourceType) return null
  switch (sourceType) {
    case SourceType.Podcast:
    case SourceType.AudioCall:
    case SourceType.VoiceNote:
    case SourceType.Webinar:
    case SourceType.AudioFile:
      return 'audio'
    case SourceType.YouTubeVideo:
    case SourceType.InstagramReel:
    case SourceType.TikTok:
    case SourceType.YouTubeShort:
    case SourceType.SalesDemo:
    case SourceType.MeetingVideo:
    case SourceType.VideoFile:
      return 'video'
    case SourceType.File:
      return 'file'
    case SourceType.Website:
      return 'website'
    default:
      return null
  }
}

export const Source = ({
  initialStep = 0,
  isCampaign,
  setIsCampaign,
  onContinue,
  sourceType,
  setSourceType,
  isFromDoc,
  initialIsCampaign,
}: Props) => {
  const formikProps = useFormikContext<Values>()
  const { transcription_id: transcriptionId, content_url: contentUrl, file } = formikProps.values
  const { isMobile } = useResponsiveDesign()
  const [document, setDocument] = useState<Doc | undefined>(undefined)
  const [pdf, setPdf] = useState<File | undefined>(undefined)
  const { isValid, errorMessage } = useIsSourceValid({ sourceType, contentUrl, file, document, pdf })
  const [currentStep, setCurrentStep] = useState(initialStep)
  const { openModal, closeModal } = useModals()
  const { fetchReference } = useFetchReference()
  const { mutateAsync: createMediaObjects, isLoading: isUploading } = useCreateMediaObjects()
  const { mutateAsync: createTranscription } = useCreateTranscription()
  const [scrapingError, setScrapingError] = useState<WebsiteScrapingErrorCode>()
  const { data: transcription } = useTranscription(transcriptionId)

  const uploadFile = async (file: File) => {
    const mediaObjects = await createMediaObjects([{ filename: file.name, file }])
    return mediaObjects[0].id
  }

  const handleSubmit = async () => {
    if (sourceType === SourceType.Website) {
      if (scrapingError && formikProps.values.focus) {
        goToFinalStep()
      } else {
        analyzeContent()
      }
      return
    }

    if (sourceType === SourceType.File && pdf) {
      analyzePdf()
      return
    }
    if (sourceType === SourceType.File) {
      analyzeDocument()
      return
    }

    if (!file) return
    setCurrentStep(2)
    try {
      const mediaObjectId = await uploadFile(file)

      const transcription = await createTranscription({ media_object_id: mediaObjectId, language: 'en' })
      formikProps.setFieldValue('transcription_id', transcription.id)
    } catch (error) {
      setCurrentStep(1)
      showToastMessage('Failed to upload file', 'error')
      reportError(error)
    }
  }

  const goToFinalStep = useCallback(() => {
    if (initialIsCampaign) {
      // skip the plurality step if it's a campaign
      onContinue()
    } else {
      setCurrentStep(3)
    }
  }, [initialIsCampaign, onContinue])

  useEffect(() => {
    if (transcription?.status === 'successful' && transcription.content) {
      showToastMessage('Transcription completed', 'success')
      goToFinalStep()
    } else if (transcription?.status === 'failed') {
      showToastMessage(transcription.failure_reason || 'Transcription failed', 'error')
      setCurrentStep(1)
    }
  }, [transcription, goToFinalStep])

  const onAbortScanWebsite = () => {
    const error = new Error('Scanning website was aborted.')
    openModal(AiErrorModal, {
      onCancel: () => {
        closeModal()
        setCurrentStep(1)
      },
      onTryAgain: () => {
        closeModal()
        analyzeContent()
      },
      error,
    })
    reportError(error)
  }

  const analyzeDocument = async () => {
    if (!document) {
      return
    }

    setCurrentStep(2)

    ArticleClient.getArticleAsMarkdown(document.id).then((response) => {
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(`Failed to extract doc text: ${response}`)
      }
      const newFocus = `${CAMPAIGN_FOCUS}${response.data.markdown}`
      formikProps.setFieldValue('focus', newFocus)
      formikProps.setFieldValue('topic', newFocus)

      goToFinalStep()
    })
  }

  const analyzePdf = async () => {
    if (!pdf) {
      return
    }

    setCurrentStep(2)

    const formData = new FormData()
    formData.append('pdf_file', pdf)
    const response = await sendToApi<{ text: string }>('/extract-pdf-text', {
      method: 'POST',
      body: formData,
    })

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      if (response.type === 'error' && [413, 422].includes(response.status)) {
        showToastMessage('PDF is too large, please upload a smaller file', 'error')
      } else {
        showToastMessage('Failed to extract text from PDF', 'error')
      }
      setCurrentStep(1)
      return
    }

    const newFocus = `${CAMPAIGN_FOCUS}${response.data.text}`
    formikProps.setFieldValue('focus', newFocus)
    formikProps.setFieldValue('topic', newFocus)

    goToFinalStep()
  }

  const analyzeContent = async () => {
    if (!contentUrl) {
      return
    }

    setCurrentStep(2)

    try {
      const websiteText = await fetchReference(contentUrl, onAbortScanWebsite)
      if (!websiteText) {
        return
      } else {
        const newFocus = `${CAMPAIGN_FOCUS}${websiteText}`
        formikProps.setFieldValue('topic', newFocus)
        formikProps.setFieldValue('focus', newFocus)
        goToFinalStep()
      }
    } catch (error) {
      if (error instanceof WebsiteScrapingError) {
        setScrapingError(error.errorCode as WebsiteScrapingErrorCode)
        setCurrentStep(1)
      }
    }
  }

  const setContentUrl = (url: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    formikProps.setFieldValue('content_url', url)
  }

  const renderInput = () => {
    switch (getInputType(sourceType)) {
      case 'audio':
        return (
          <AudioOrVideoSource
            sourceType="audio"
            file={file}
            setFile={(file: File | undefined) => {
              formikProps.setFieldValue('file', file)
            }}
          />
        )
      case 'video':
        return (
          <AudioOrVideoSource
            sourceType="video"
            file={file}
            setFile={(file: File | undefined) => {
              formikProps.setFieldValue('file', file)
            }}
          />
        )
      case 'file':
        return <DocumentSource setDocument={setDocument} document={document} setPdf={setPdf} pdf={pdf} />
      case 'website':
        return (
          <WebsiteSource
            formikProps={formikProps}
            contentUrl={contentUrl}
            isValid={isValid}
            analyzeContent={analyzeContent}
            setContentUrl={setContentUrl}
            scrapingError={scrapingError}
            errorMessage={errorMessage}
          />
        )
      default:
        return
    }
  }

  if (currentStep === 0) {
    return (
      <Form.Root>
        <Form.Section>
          <Form.SectionContent>
            <ChooseSourceType sourceType={sourceType || SourceType.Website} setSourceType={setSourceType} />
          </Form.SectionContent>
        </Form.Section>
        <Form.Actions isSticky>
          <Form.PrimaryActions>
            <Button size="lg" endIcon={ArrowRight} fullWidth={isMobile} onPress={() => setCurrentStep(1)}>
              Continue
            </Button>
          </Form.PrimaryActions>
        </Form.Actions>
      </Form.Root>
    )
  }

  if (currentStep === 1) {
    return (
      <div className={Styles.formContainer}>
        <Form.Root>
          <Form.Section>
            <Form.SectionContent>{renderInput()}</Form.SectionContent>
          </Form.Section>
          <Form.Actions isSticky>
            {initialIsCampaign && (
              <Form.PreviousButton onPress={() => setCurrentStep(0)}>Previous Step</Form.PreviousButton>
            )}
            <Form.PrimaryActions>
              <Button size="lg" onPress={handleSubmit} endIcon={ArrowRight} fullWidth={isMobile} isDisabled={!isValid}>
                Analyze Content
              </Button>
            </Form.PrimaryActions>
          </Form.Actions>
        </Form.Root>
      </div>
    )
  }

  const animationProps: LottieProps = {
    play: true,
    style: {
      height: 150,
      width: 150,
    },
  }

  let loaderProps: LoaderProps =
    sourceType === SourceType.Website || sourceType === SourceType.File
      ? {
          icon: <Analyzing {...animationProps} />,
          text: 'Analyzing text content',
        }
      : {
          icon: <Uploading {...animationProps} />,
          text: 'Uploading',
        }

  if (isUploading) {
    loaderProps = {
      icon: <Uploading {...animationProps} />,
      text: 'Uploading',
    }
  } else if (transcription && transcription.status === 'started') {
    loaderProps = {
      icon: <Transcribing {...animationProps} />,
      text: 'Transcribing',
    }
  }

  if (currentStep === 2) {
    return (
      <Form.Root>
        <Form.Section>
          <Form.SectionContent>
            <Loader {...loaderProps} />
          </Form.SectionContent>
        </Form.Section>
        <Form.Actions isSticky>
          {initialIsCampaign && (
            <Form.PreviousButton onPress={() => setCurrentStep(0)}>Previous Step</Form.PreviousButton>
          )}
          <Form.PrimaryActions>
            <Button size="lg" endIcon={ArrowRight} fullWidth={isMobile} isDisabled>
              {loaderProps.text}
            </Button>
          </Form.PrimaryActions>
        </Form.Actions>
      </Form.Root>
    )
  }

  if (currentStep === 3) {
    return (
      <ChooseContentPlurality
        isMulti={isCampaign}
        onChange={setIsCampaign}
        onContinue={onContinue}
        isFromDoc={isFromDoc}
        transcribedText={transcription?.content || ''}
        goBack={() => setCurrentStep(1)}
      />
    )
  }

  return null
}
